import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, CardBody, CardTitle, Button, ButtonGroup } from 'reactstrap';
import { useMutation, useQueryClient } from 'react-query';
import { useRole } from 'hooks/useRole';
import { useAuth } from 'hooks/useAuth';
import moment from 'moment';
import Apaexlinecolumn from '../../AllCharts/apex/apaexlinecolumn';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import MiniWidget from './mini-widget';
import { useSelector, useDispatch } from 'react-redux';
import LineApexChart from '../../AllCharts/apex/chartapex';
import TableContainer from '../../../components/Common/TableContainer';
import { getAllAllMembersApi } from 'api/member';
import { MemberId, BillingName, Date, PaymentStatus, PolicyStatus } from './MemberCol';
import { getMemberStatusCountsApi, getAllAllMembersReportsApi } from 'api/report';
import { setMemberSummary } from 'store/actions';
import { getSearchColumns, sumArray } from 'utils/tools';
import MainFilter from 'components/Common/MainFilter';

//Members Chart
const series1 = [
  { name: 'Members', data: [12, 14, 2, 47, 42, 15, 47, 75, 65, 19, 14] },
];
const options1 = {
  chart: { sparkline: { enabled: !0 } },
  stroke: { curve: 'smooth', width: 2 },
  colors: ['#f1b44c'],
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      inverseColors: false,
      opacityFrom: 0.45,
      opacityTo: 0.05,
      stops: [25, 100, 100, 100],
    },
  },
  tooltip: {
    fixed: { enabled: false },
    x: { show: false },
    marker: { show: false },
  },
};

//Dependants Chart
const series2 = [
  { name: 'Dependants', data: [25, 66, 41, 89, 63, 25, 44, 12, 36, 9, 54] },
];
const options2 = {
  chart: { sparkline: { enabled: !0 } },
  stroke: { curve: 'smooth', width: 2 },
  colors: ['#00b3e4'],
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      inverseColors: false,
      opacityFrom: 0.45,
      opacityTo: 0.05,
      stops: [25, 100, 100, 100],
    },
  },
  tooltip: {
    fixed: { enabled: false },
    x: { show: false },
    marker: { show: false },
  },
};

//Defaulted Chart
const series3 = [
  { name: 'Defaulted', data: [35, 53, 93, 47, 54, 24, 47, 75, 65, 19, 14] },
];
const options3 = {
  chart: { sparkline: { enabled: !0 } },
  stroke: { curve: 'smooth', width: 2 },
  colors: ['#50a5f1'],
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      inverseColors: false,
      opacityFrom: 0.45,
      opacityTo: 0.05,
      stops: [25, 100, 100, 100],
    },
  },
  tooltip: {
    fixed: { enabled: false },
    x: { show: false },
    marker: { show: false },
  },
};

//Defaulted Chart
const series4 = [
  { name: 'Individual', data: [35, 53, 93, 47, 54, 24, 47, 75, 65, 19, 14] },
];
const options4 = {
  chart: { sparkline: { enabled: !0 } },
  stroke: { curve: 'smooth', width: 2 },
  colors: ['#50a5f1'],
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      inverseColors: false,
      opacityFrom: 0.45,
      opacityTo: 0.05,
      stops: [25, 100, 100, 100],
    },
  },
  tooltip: {
    fixed: { enabled: false },
    x: { show: false },
    marker: { show: false },
  },
};

const MemberReports = () => {
  const { isAuthenticated } = useAuth();
  const {
    isFacilitator,
    isSiteCord,
    isCountyCoordinator,
    isClusterLeader,
    isFinance,
    isDirector,
    addresses,
  } = useRole();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { memberSummary } = useSelector((state) => ({
    memberSummary: state.reports.members,
  }));

  const [pagination, setPagination] = useState({
    pageSize: 10,
    pageNumber: 1,
    sortBy: 'id',
    ascendingSort: false,
  });
  const [timePeriod, setTimePeriod] = useState('year');

  const reports = useMemo(() => {
    const currentMonthData = memberSummary?.length > 0 ? memberSummary[memberSummary.length - 1] : undefined;

    return [
      {
        title: 'Members',
        icon: 'mdi mdi-account',
        color: 'primary',
        value: currentMonthData?.totalMembers
          ? currentMonthData.totalMembers.toLocaleString()
          : '0',
        desc: 'Total Members',
        series: series1,
        options: options1,
        arrowUpDown: 'mdi mdi-arrow-up ms-1 text-success',
      },
      {
        title: 'Dependants',
        icon: 'mdi mdi-account-multiple',
        color: 'info',
        value: currentMonthData?.totalDependents
          ? currentMonthData.totalDependents.toLocaleString()
          : '0',
        desc: 'Total Dependants',
        series: series2,
        options: options2,
        arrowUpDown: 'mdi mdi-arrow-down ms-1 text-danger',
      },
      {
        title: 'New Members',
        icon: 'mdi mdi-account-multiple-plus',
        color: 'success',
        value: currentMonthData?.newMembers
          ? currentMonthData.newMembers.toLocaleString()
          : '0',
        desc: 'New Members',
        series: series3,
        options: options3,
        arrowUpDown: 'mdi mdi-arrow-up ms-1 text-success',
      },
      {
        title: 'Individual Members',
        icon: 'mdi mdi-account-multiple-plus',
        color: 'success',
        value: currentMonthData?.individualMembers
          ? currentMonthData.individualMembers.toLocaleString()
          : '0',
        desc: 'Individual Members',
        series: series4,
        options: options4,
        arrowUpDown: 'mdi mdi-arrow-up ms-1 text-success',
      },
    ];
  }, [memberSummary]);

  const columnChartSeries = useMemo(() => {
    if (timePeriod === 'year') {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const currentMonthIndex = moment().month();
      const fullYearData = months.slice(0, currentMonthIndex + 1).map((month) => {
        const summary = memberSummary.find((s) => s.monthName === month);
        return {
          totalMembers: summary?.totalMembers || 0,
          totalDependents: summary?.totalDependents || 0,
          newMembers: summary?.newMembers || 0,
        };
      });
      return [
        { name: 'Total Members', data: fullYearData.map((d) => d.totalMembers) },
        { name: 'Dependants', data: fullYearData.map((d) => d.totalDependents) },
        { name: 'New Members', data: fullYearData.map((d) => d.newMembers) },
      ];
    }
    // Default case is now explicitly 'month'
    const currentMonthData = memberSummary.filter((s) => s.monthName === moment().format('MMM'));
    return [
      { name: 'Total Members', data: currentMonthData.map((c) => c.totalMembers || 0) },
      { name: 'Dependants', data: currentMonthData.map((c) => c.totalDependents || 0) },
      { name: 'New Members', data: currentMonthData.map((c) => c.newMembers || 0) },
    ];
  }, [memberSummary, timePeriod]);

  const {
    mutate: mutateMemberCounts,
    isLoading: isLoadingProducts,
    data,
  } = useMutation((payload) => getMemberStatusCountsApi(payload), {
    onSuccess: (res) => {
      console.log('MemberSummary Response', res);
      dispatch(setMemberSummary(res?.data));
    },
    onSettled: () => {
      queryClient.invalidateQueries('get-all-counties');
    },
  });

  const fetchDataForPeriod = (period) => {
    let fromDate, toDate;
    if (period === 'month') {
      fromDate = moment().startOf('month').format('DD-MM-YYYY');
      toDate = moment().format('DD-MM-YYYY');
    } else if (period === 'year') {
      fromDate = moment().startOf('year').format('DD-MM-YYYY');
      toDate = moment().format('DD-MM-YYYY');
    }
    mutateMemberCounts({
      fromDate,
      toDate,
      address: {},
    });
  };

  useEffect(() => {
    fetchDataForPeriod(timePeriod);
    // mutateMembers({ ...pagination, pageNumber: pagination.pageNumber - 1 });
  }, [pagination, timePeriod]);

  const onSearch = (data) => {
    const addressData = {};
    if (isFacilitator) {
      addressData['wardId'] = addresses[0]?.wardId;
    }
    if (isSiteCord && data?.wardId) {
      addressData['wardId'] = data?.wardId.value;
    }
    if (data?.wardId) {
      addressData['wardIds'] = [data?.wardId.value];
    }
    if (data?.constituencyId) {
      addressData['constituencyIds'] = [data?.constituencyId.value];
    }
    if (data?.countyId) {
      addressData['countyIds'] = [data?.countyId.value];
    }
    if (data?.regionId) {
      addressData['regionIds'] = [data?.regionId.value];
    }
    // }
    mutateMemberCounts({
      fromDate: data?.startDate?.value,
      toDate: data?.endDate?.value,
      wardIds: getSearchColumns()?.find((t) => t.name === 'wardIds')?.value,
      address: addressData,
      ...addressData
    });
    // mutateMembers({
    //   ...pagination,
    //   pageNumber: pagination.pageNumber - 1,
    //   searchColumns: Object.entries(addressData).map(([name, value]) => ({ name, value })),
    // });
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Reports" breadcrumbItem="Member Reports" />
        <MainFilter pagination={pagination} onSearch={onSearch} showDateFilter />
        <Row>
          <MiniWidget reports={reports} />
        </Row>
        <Row>
          <Col md={12}>
            <Card>
              <CardBody>
                <CardTitle className="mb-4">Members Summary</CardTitle>
                {/* Time Period Buttons */}
                <ButtonGroup className="mb-3">
                  <Button
                    color={timePeriod === 'month' ? 'primary' : 'secondary'}
                    onClick={() => setTimePeriod('month')}
                  >
                    Month
                  </Button>
                  <Button
                    color={timePeriod === 'year' ? 'primary' : 'secondary'}
                    onClick={() => setTimePeriod('year')}
                  >
                    Year
                  </Button>
                </ButtonGroup>
                <Apaexlinecolumn
                  dataColors='["--bs-primary","--bs-success","--bs-primary"]'
                  series={columnChartSeries}
                  categories={
                    timePeriod === 'year'
                      ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].slice(
                        0,
                        moment().month() + 1
                      )
                      : [moment().format('MMM')]
                  }
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default MemberReports;
